import { useMemo } from 'react';
import { DataError, useList } from '@hana/data';
import type { ScreenState } from '@hana/ui';
import { useMeeAccess } from './access';
import { BOARD_NOW, BOARD_TODAY, meeCalendar, type MeePerson } from './data';
import { useClockOuts } from './clock';
import { useMeeRequests } from './requests';
import { withClockOut } from './time/month';

// TODO: "hôm nay" / "bây giờ" thật khi đổi sang dữ liệu thật; dữ liệu mẫu của board neo 22/Oct 15:20.
const cal = meeCalendar();

/** Dữ liệu chung của các màn Mee: người xem, đơn, lịch công ty và trạng thái màn (board `state`). */
export function useMee() {
  const access = useMeeAccess();
  const people = useList('meePerson');
  const reqs = useMeeRequests();
  const me: MeePerson | undefined = people.data?.items.find((p) => p.id === access.viewer);
  const att = useList('meeAttendance', { where: { owner: access.viewer } });
  const clock = useClockOuts();
  const attendance = useMemo(() => {
    const base = att.data?.items ?? [];
    const days = new Set(base.map((a) => a.day));
    const mapped = base.map((a) => (clock.map.has(`${a.owner}:${a.day}`) ? withClockOut(cal, a.owner, a.day, clock.map.get(`${a.owner}:${a.day}`)!, a) : a));
    const extra = [...clock.map].filter(([k]) => k.startsWith(`${access.viewer}:`) && !days.has(k.slice(access.viewer.length + 1)));
    return [...mapped, ...extra.map(([k, out]) => withClockOut(cal, access.viewer, k.slice(access.viewer.length + 1), out))];
  }, [att.data, clock.map, access.viewer]);
  const err = people.error ?? reqs.q.error ?? att.error;
  const loading = people.isPending || reqs.q.isPending || att.isPending;
  const state: ScreenState = useMemo(() => {
    if (access.force) return access.force;
    if (err) return err instanceof DataError && err.code === 'noperm' ? 'noperm' : 'error';
    if (loading) return 'loading';
    return me ? 'ready' : 'empty';
  }, [access.force, err, loading, me]);
  // Đơn phép từ worker đã lọc phạm vi theo phiên (mã nhân viên thật có thể khác `viewer` mẫu) → tin theo tập id của worker.
  const mine = useMemo(() => reqs.items.filter((r) => r.owner === access.viewer || reqs.mineIds.has(r.id)), [reqs.items, reqs.mineIds, access.viewer]);
  const team = useMemo(() => reqs.items.filter((r) => reqs.teamIds.has(r.id) || (me?.manager && me.team.includes(r.owner))), [reqs.items, reqs.teamIds, me]);
  return {
    state,
    me,
    cal,
    today: BOARD_TODAY,
    now: BOARD_NOW,
    mine,
    team,
    attendance,
    clockOut: (t: string) => clock.clockOut(access.viewer, BOARD_TODAY, t),
    pull: reqs.pull,
    submit: reqs.submit,
    decide: reqs.decide,
    people: people.data?.items ?? [],
    all: reqs.items,
    retry: () => void Promise.all([people.refetch(), reqs.q.refetch(), att.refetch()]),
  };
}
